import React, { useState } from 'react';
import { Lock } from 'lucide-react';
import {
  VocabData,
  BASE_REL_TYPES,
  BASE_ROLE_TYPES,
  BASE_ARCHETYPES,
} from '../constants/vocab';

interface VocabularyDrawerBodyProps {
  vocab: VocabData;
  onUpdate: (updated: VocabData) => Promise<void>;
  activeTab: 'relTypes' | 'roles' | 'archetypes';
  setActiveTab: (t: 'relTypes' | 'roles' | 'archetypes') => void;
}

const PAIR_LABELS: Array<{
  key: string;
  sourceLabel: string;
  targetLabel: string;
  sourceClass: string;
  targetClass: string;
  arrow: string;
}> = [
  { key: 'char-char',   sourceLabel: 'Character', targetLabel: 'Character', sourceClass: 'char', targetClass: 'char', arrow: '↔' },
  { key: 'char-event',  sourceLabel: 'Character', targetLabel: 'Event',     sourceClass: 'char', targetClass: 'evt',  arrow: '→' },
  { key: 'char-loc',    sourceLabel: 'Character', targetLabel: 'Location',  sourceClass: 'char', targetClass: 'loc',  arrow: '→' },
  { key: 'loc-event',   sourceLabel: 'Location',  targetLabel: 'Event',     sourceClass: 'loc',  targetClass: 'evt',  arrow: '→' },
  { key: 'loc-loc',     sourceLabel: 'Location',  targetLabel: 'Location',  sourceClass: 'loc',  targetClass: 'loc',  arrow: '↔' },
  { key: 'event-event', sourceLabel: 'Event',     targetLabel: 'Event',     sourceClass: 'evt',  targetClass: 'evt',  arrow: '↔' },
];

const VocabularyDrawerBody: React.FC<VocabularyDrawerBodyProps> = ({
  vocab,
  onUpdate,
  activeTab,
  setActiveTab,
}) => {
  const [addInputs, setAddInputs] = useState<Record<string, string>>({});
  const [roleInput, setRoleInput] = useState('');
  const [archetypeInput, setArchetypeInput] = useState('');

  const handleAddRelType = async (key: string) => {
    const val = (addInputs[key] ?? '').trim();
    if (!val) return;
    const current = vocab.customRelTypes[key] ?? [];
    if (current.includes(val)) return;
    try {
      await onUpdate({
        ...vocab,
        customRelTypes: { ...vocab.customRelTypes, [key]: [...current, val] },
      });
      setAddInputs(prev => ({ ...prev, [key]: '' }));
    } catch (err) {
      console.error('Failed to save vocabulary update', err);
    }
  };

  const handleDeleteRelType = async (key: string, val: string) => {
    const current = vocab.customRelTypes[key] ?? [];
    try {
      await onUpdate({
        ...vocab,
        customRelTypes: { ...vocab.customRelTypes, [key]: current.filter(v => v !== val) },
      });
    } catch (err) {
      console.error('Failed to save vocabulary update', err);
    }
  };

  const handleAddRole = async () => {
    const val = roleInput.trim();
    if (!val || vocab.customRoleTypes.includes(val)) return;
    try {
      await onUpdate({ ...vocab, customRoleTypes: [...vocab.customRoleTypes, val] });
      setRoleInput('');
    } catch (err) {
      console.error('Failed to save vocabulary update', err);
    }
  };

  const handleDeleteRole = async (val: string) => {
    try {
      await onUpdate({ ...vocab, customRoleTypes: vocab.customRoleTypes.filter(v => v !== val) });
    } catch (err) {
      console.error('Failed to save vocabulary update', err);
    }
  };

  const handleAddArchetype = async () => {
    const val = archetypeInput.trim();
    if (!val || vocab.customArchetypes.includes(val)) return;
    try {
      await onUpdate({ ...vocab, customArchetypes: [...vocab.customArchetypes, val] });
      setArchetypeInput('');
    } catch (err) {
      console.error('Failed to save vocabulary update', err);
    }
  };

  const handleDeleteArchetype = async (val: string) => {
    try {
      await onUpdate({ ...vocab, customArchetypes: vocab.customArchetypes.filter(v => v !== val) });
    } catch (err) {
      console.error('Failed to save vocabulary update', err);
    }
  };

  return (
    <div className="rt-vocab-drawer-body">
      <div className="rt-subtabs">
        <button
          className={`rt-subtab${activeTab === 'relTypes' ? ' active' : ''}`}
          onClick={() => setActiveTab('relTypes')}
        >
          Relationship types
        </button>
        <button
          className={`rt-subtab${activeTab === 'roles' ? ' active' : ''}`}
          onClick={() => setActiveTab('roles')}
        >
          Roles
        </button>
        <button
          className={`rt-subtab${activeTab === 'archetypes' ? ' active' : ''}`}
          onClick={() => setActiveTab('archetypes')}
        >
          Archetypes
        </button>
      </div>

      <div className="rt-vocab-content">
        {activeTab === 'relTypes' && (
          <>
            <p className="rt-vocab-helptext">
              Types of relationship that can connect entities on the canvas. Base types are locked; add custom types as your story needs them.
            </p>
            {PAIR_LABELS.map(pair => (
              <div key={pair.key} className="rt-vocab-group">
                <div className="vocab-pair-header">
                  <span className={`vocab-pair-badge ${pair.sourceClass}`}>{pair.sourceLabel}</span>
                  <span className="vocab-pair-arrow">{pair.arrow}</span>
                  <span className={`vocab-pair-badge ${pair.targetClass}`}>{pair.targetLabel}</span>
                </div>
                <div className="vocab-pair-body">
                  <div className="rt-vocab-chips">
                    {(BASE_REL_TYPES[pair.key] ?? []).map(v => (
                      <span key={v} className="rt-vocab-chip base">
                        {v}{' '}
                        <Lock size={10} color="#9CA3AF" aria-hidden={true} />
                      </span>
                    ))}
                    {(vocab.customRelTypes[pair.key] ?? []).map(v => (
                      <span key={v} className="rt-vocab-chip custom">
                        {v}
                        <button
                          className="vocab-chip-del"
                          aria-label={`Remove ${v}`}
                          onClick={() => handleDeleteRelType(pair.key, v)}
                        >✕</button>
                      </span>
                    ))}
                  </div>
                  <div className="vocab-add-row">
                    <input
                      type="text"
                      placeholder="Add a custom type…"
                      value={addInputs[pair.key] ?? ''}
                      onChange={e => setAddInputs(prev => ({ ...prev, [pair.key]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') handleAddRelType(pair.key); }}
                    />
                    <button onClick={() => handleAddRelType(pair.key)}>Add</button>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {activeTab === 'roles' && (
          <>
            <p className="rt-vocab-helptext">
              Roles describe how a character functions in your story (Protagonist, Mentor, Antagonist). Applied on a character's detail panel.
            </p>
            <div className="rt-vocab-chips">
              {BASE_ROLE_TYPES.map(v => (
                <span key={v} className="rt-vocab-chip base">
                  {v}{' '}
                  <Lock size={10} color="#9CA3AF" aria-hidden={true} />
                </span>
              ))}
              {vocab.customRoleTypes.map(v => (
                <span key={v} className="rt-vocab-chip custom">
                  {v}
                  <button
                    className="vocab-chip-del"
                    aria-label={`Remove ${v}`}
                    onClick={() => handleDeleteRole(v)}
                  >✕</button>
                </span>
              ))}
            </div>
            <div className="vocab-add-row">
              <input
                type="text"
                placeholder="Add a custom role…"
                value={roleInput}
                onChange={e => setRoleInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddRole(); }}
              />
              <button onClick={handleAddRole}>Add</button>
            </div>
          </>
        )}

        {activeTab === 'archetypes' && (
          <>
            <p className="rt-vocab-helptext">
              Archetypes describe the kind of character at a glance (The Hero, The Sage). Useful for outline work and casting.
            </p>
            <div className="rt-vocab-chips">
              {BASE_ARCHETYPES.map(v => (
                <span key={v} className="rt-vocab-chip base">
                  {v}{' '}
                  <Lock size={10} color="#9CA3AF" aria-hidden={true} />
                </span>
              ))}
              {vocab.customArchetypes.map(v => (
                <span key={v} className="rt-vocab-chip custom">
                  {v}
                  <button
                    className="vocab-chip-del"
                    aria-label={`Remove ${v}`}
                    onClick={() => handleDeleteArchetype(v)}
                  >✕</button>
                </span>
              ))}
            </div>
            <div className="vocab-add-row">
              <input
                type="text"
                placeholder="Add a custom archetype…"
                value={archetypeInput}
                onChange={e => setArchetypeInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddArchetype(); }}
              />
              <button onClick={handleAddArchetype}>Add</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VocabularyDrawerBody;
